import classNames from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChangeInstrumentModal } from '@pages/Trade/Markets/Charts/modals/ChangeInstrumentModal'
import { FullScreenIcon } from '@pages/Trade/Markets/Charts/utils/Fullscreen.icon'
import {
  DEFAULT_CHART_TIMEFRAME,
  DEFAULT_CHART_TIMEZONE,
} from '@pages/Trade/Markets/Charts/utils/utils'
import { useCurrentPriceForTrade } from '@pages/Trade/Portfolio/hooks/useCurrentPricesStore'
import { Account } from '@pages/Trade/types'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { useModal } from '@context/ModalsContext'
import {
  getEffectiveTheme,
  useSessionLanguage,
  useSessionTheme,
} from '@context/SessionSettingsContext'
import { useSidebar } from '@context/SidebarContext'

import { ActionIcon } from '@ui/components/ActionIcon'
import { Button, IconButton } from '@ui/components/Button'
import { EditIcon } from '@ui/icons/Edit.icon'

import { ClientApiClient } from '@utils/clientApi'

import { DxTradePosition } from '@model/DxTrade.types'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { safeLocalStorage } from '@hooks/useStorage'

import { createDxtradeDatafeed } from '../../../../tradeview/datafeed'
import { loadTradingView } from '../../utils/loadTradingView'

import styles from './TradingViewChart.module.scss'

type ChartApi = ReturnType<ReturnType<typeof window.TradingView.widget>['chart']>
interface TradingViewChartProps {
  apiClient: ClientApiClient
  account: Account
  symbol: string

  /**
   * chart height, accepts a number (pixels) or a CSS length string
   * when a number is provided, it is applied directly as `${height}px`
   */
  height?: string | number
  width?: string | number

  isMobile?: boolean
  isFullscreen?: boolean
  isWidgetOpen?: boolean

  collapsed?: boolean
  openPositions?: DxTradePosition[]

  onTradeClick?: (side: TradeSide, symbol: string) => void
  onToggleFullscreen?: () => void
  onSelectInstrument?: (value: string) => void
  timeframe?: string
  onTimeframeChange?: (value: string) => void
  timezone?: string
  onTimezoneChange?: (value: string) => void
  priceIncrement?: number | null
}
const CHART_STATE_STORAGE_KEY = 'markets.chartState.v1'
type TradeSide = 'BUY' | 'SELL'
type ChartTheme = 'light' | 'dark'

/** min height TV charting library renders comfortably */
export const TV_MIN_CHART_HEIGHT_PX = 250

const TV_LIBRARY_PATH = 'https://cdn.tickmill.com/tradingview/v30.3.0/charting_library/'
const TABLET_BREAKPOINT = 1024

const THEME_COLORS = {
  light: { bg: '#ffffff', fg: '#000000' },
  dark: { bg: '#000000', fg: '#ffffff' },
} as const

const TV_LOCALE_MAP: Record<string, string> = {
  en: 'en',
  ru: 'ru',
  pl: 'pl',
  de: 'de',
  fr: 'fr',
  es: 'es',
  it: 'it',
  pt: 'pt',
  tr: 'tr',
  ja: 'ja',
  tw: 'zh_TW',
  zh: 'zh',
  vi: 'vi',
  ms: 'ms_MY',
  ko: 'ko',
  id: 'id_ID',
  ar: 'ar',
  th: 'th',
}

const DISABLED_FEATURES = [
  'left_toolbar',
  'header_compare',
  'header_undo_redo',
  'header_screenshot',
  'border_around_the_chart',
  'header_fullscreen_button',
  'header_symbol_search',
  'legend_inplace_edit',
] as const

const ENABLED_FEATURES = ['header_widget', 'timeframes_toolbar'] as const

export const TradingViewChart: React.FC<TradingViewChartProps> = ({
  apiClient,
  account,
  symbol,
  collapsed = false,
  isMobile = false,
  onTradeClick,
  isFullscreen = false,
  onToggleFullscreen,
  isWidgetOpen = false,
  onSelectInstrument,
  height = TV_MIN_CHART_HEIGHT_PX,
  width = '100%',
  timeframe = DEFAULT_CHART_TIMEFRAME,
  onTimeframeChange,
  openPositions,
  timezone = DEFAULT_CHART_TIMEZONE,
  onTimezoneChange,
  priceIncrement,
}) => {
  const sessionLang = useSessionLanguage()

  const chartContainerRef = useRef<HTMLDivElement>(null)
  const onTimeframeChangeRef = useRef(onTimeframeChange)
  onTimeframeChangeRef.current = onTimeframeChange
  const onTimezoneChangeRef = useRef(onTimezoneChange)
  onTimezoneChangeRef.current = onTimezoneChange
  const [error, setError] = useState<string | null>(null)
  const [isChartMenuOpen, setIsChartMenuOpen] = useState(false)
  const [isTablet, setIsTablet] = useState(() => window.innerWidth <= TABLET_BREAKPOINT)
  const { isExpanded: isSidebarExpanded } = useSidebar()
  const accountCode = account.platformOverview?.login
  const isCompactLayout = isTablet && isSidebarExpanded

  useEffect(() => {
    const updateTabletState = () => setIsTablet(window.innerWidth <= TABLET_BREAKPOINT)

    updateTabletState()
    window.addEventListener('resize', updateTabletState)

    return () => window.removeEventListener('resize', updateTabletState)
  }, [])

  const positionsForSymbol = useMemo(
    () =>
      openPositions?.filter((position) => {
        const positionAccountCode = position.account.split(':').at(-1)

        return position.symbol === symbol && positionAccountCode === accountCode
      }) ?? [],
    [openPositions, symbol, accountCode]
  )
  const positionsKey = positionsForSymbol
    .map((p) => `${p.positionCode}:${p.side}:${p.quantity}:${p.openPrice}:${p.openTime}`)
    .join('|')
  const widgetRef = useRef<ReturnType<typeof window.TradingView.widget> | null>(null)
  const [isChartReady, setIsChartReady] = useState(false)
  useEffect(() => {
    const getIframeDocument = () => {
      const iframe = chartContainerRef.current?.querySelector('iframe')
      return iframe?.contentDocument || iframe?.contentWindow?.document
    }

    const checkMenu = () => {
      const iframeDoc = getIframeDocument()
      if (!iframeDoc) return

      const iframeMenu = iframeDoc.querySelector('[class*="menuBox"]')
      const settingsDialog = iframeDoc.querySelector('[data-qa-id="ui-lib-PopupDialog"]')

      setIsChartMenuOpen(!!(iframeMenu || settingsDialog))
    }
    const observer = new MutationObserver(checkMenu)

    const interval = setInterval(() => {
      const iframeDoc = getIframeDocument()
      if (iframeDoc) {
        observer.observe(iframeDoc.body, {
          childList: true,
          subtree: true,
        })
        clearInterval(interval)
      }
    }, 500)

    return () => {
      observer.disconnect()
      clearInterval(interval)
    }
  }, [symbol, timeframe, timezone])
  const changeInstrumentModal = useModal(ChangeInstrumentModal)

  const chartTheme = getEffectiveTheme(useSessionTheme())
  const tvLocale = TV_LOCALE_MAP[sessionLang] ?? 'en'
  const chartHeight = isMobile ? '100%' : toCssLength(height)
  const saveChartState = (widget: ReturnType<typeof window.TradingView.widget>) => {
    widget.save((state: Record<string, unknown>) => {
      try {
        safeLocalStorage.setItem(CHART_STATE_STORAGE_KEY, JSON.stringify(state))
      } catch {
        console.debug('Cannot save TradingView chart state')
      }
    })
  }

  const readChartState = () => {
    try {
      const raw = safeLocalStorage.getItem(CHART_STATE_STORAGE_KEY)

      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }
  const drawOpenPositions = async (chart: ChartApi, positions: DxTradePosition[]) => {
    await Promise.all(
      positions.map((position) =>
        chart.createShape(
          {
            price: position.openPrice,
          },
          {
            shape: 'horizontal_line',
            text: `${position.side} ${position.quantity} @ ${position.openPrice}`,
            lock: true,
            disableSelection: true,
            disableSave: true,
            overrides: {
              color: position.side === 'BUY' ? '#93FCC0' : '#FF80A2',
              textcolor: position.side === 'BUY' ? '#22D46E' : '#E6003D',
              linecolor: position.side === 'BUY' ? '#93FCC0' : '#FF80A2',
            },
          }
        )
      )
    )
  }
  const tryInject = () => {
    const iframe = chartContainerRef.current?.querySelector('iframe') as HTMLIFrameElement | null
    if (iframe?.contentDocument) {
      injectChartStyles(iframe)
    }
  }
  const symbolRef = useRef(symbol)
  const clearAllShapes = (chart: ChartApi) => {
    const shapes = chart.getAllShapes?.() ?? []

    shapes.forEach((shape: { id: string }) => {
      try {
        chart.removeEntity(shape.id)
      } catch (error) {
        console.warn('[TV] Failed to remove chart shape', {
          shapeId: shape.id,
          error,
        })
      }
    })
  }
  useEffect(() => {
    if (!chartContainerRef.current || collapsed) {
      return
    }
    setIsChartReady(false)
    let cancelled = false
    let widgetInstance: ReturnType<typeof window.TradingView.widget> | null = null

    loadTradingView()
      .then(() => {
        if (cancelled || !chartContainerRef.current || !window.TradingView) return

        const widgetOptions = buildWidgetOptions({
          container: chartContainerRef.current,
          datafeed: createDxtradeDatafeed(apiClient, account, timezone),
          symbol: symbolRef.current,
          locale: tvLocale,
          theme: chartTheme,
          timeframe,
          timezone,
        })

        widgetInstance = new window.TradingView.widget(widgetOptions)
        widgetRef.current = widgetInstance

        widgetInstance.onChartReady(async () => {
          const savedState = readChartState()

          if (savedState) {
            try {
              await widgetInstance?.load(savedState)
            } catch (error) {
              console.warn('[TV] Failed to load chart state', error)
            }
          }

          const chart = widgetInstance?.chart()
          if (!chart) return

          clearAllShapes(chart)

          const markChartReady = () => {
            if (cancelled) return

            setIsChartReady(true)
            setError(null)
          }

          if (chart.symbol() !== symbolRef.current) {
            chart.setSymbol(symbolRef.current, markChartReady)
          } else {
            markChartReady()
          }

          widgetInstance?.subscribe('onAutoSaveNeeded', () => {
            if (widgetInstance) {
              saveChartState(widgetInstance)
            }
          })

          const { bg } = THEME_COLORS[chartTheme]

          widgetInstance?.chart().applyOverrides({
            'paneProperties.backgroundType': 'solid',
            'paneProperties.background': bg,
            'scalesProperties.textColor': chartTheme === 'dark' ? '#FFFFFF' : '#0F0F0F',
          })

          tryInject()

          const timezoneApi = chart.getTimezoneApi?.()

          if (timezoneApi) {
            if (timezoneApi.getTimezone()?.id !== timezone) {
              timezoneApi.setTimezone(timezone)
            }
          } else if (chart.getTimezone() !== timezone) {
            chart.setTimezone(timezone)
          }

          if (chart.resolution?.() !== timeframe) {
            chart.setResolution?.(timeframe)
          }

          let currentInterval = timeframe
          let currentTimezone = timezone

          chart.onIntervalChanged().subscribe(null, (interval: string) => {
            if (interval === currentInterval) return

            currentInterval = interval
            onTimeframeChangeRef.current?.(interval)
          })

          if (timezoneApi) {
            timezoneApi.onTimezoneChanged().subscribe(null, (tz: string) => {
              if (tz === currentTimezone) return

              currentTimezone = tz
              onTimezoneChangeRef.current?.(tz)
            })
          }
        })
      })
      .catch((err) => {
        if (!cancelled) {
          setError(`Failed to load TradingView: ${err.message}`)
        }
      })

    return () => {
      cancelled = true
      setIsChartReady(false)
      widgetRef.current = null

      try {
        widgetInstance?.remove?.()
      } catch (error) {
        console.warn('[TV] Failed to remove widget', error)
      }
    }
  }, [collapsed, chartTheme, apiClient, account, tvLocale, timeframe, timezone])

  useEffect(() => {
    if (!isChartReady) return

    const widget = widgetRef.current
    const chart = widget?.chart()

    if (!widget || !chart) return

    let cancelled = false

    chart.dataReady(() => {
      if (cancelled || widgetRef.current !== widget) return

      clearAllShapes(chart)

      if (positionsForSymbol.length === 0) return

      void drawOpenPositions(chart, positionsForSymbol).catch((error) => {
        if (!cancelled) {
          console.warn('[TV] Failed to draw open positions', {
            accountCode,
            symbol,
            error,
          })
        }
      })
    })

    return () => {
      cancelled = true
    }
  }, [isChartReady, accountCode, symbol, positionsKey])

  useEffect(() => {
    symbolRef.current = symbol

    const chart = widgetRef.current?.chart()

    if (!chart) return

    if (chart.symbol() === symbol) {
      return
    }

    setIsChartReady(false)
    clearAllShapes(chart)

    chart.setSymbol(symbol, () => {
      setIsChartReady(true)
    })
  }, [symbol])
  const ChartControls = ({ hidden }: { hidden?: boolean }) => {
    return (
      <>
        {!collapsed && (
          <div
            className={classNames(styles.fullScreenButton, {
              [styles.hidden]: hidden,
              [styles.compact]: isCompactLayout,
            })}
          >
            <ActionIcon
              icon={{
                icon: FullScreenIcon,
                color: chartTheme === 'dark' ? 'text-white' : 'text-black',
              }}
              size={isCompactLayout ? 'x24' : 'x32'}
              onClick={() => onToggleFullscreen?.()}
            />
          </div>
        )}
      </>
    )
  }

  const TradeButtons = ({ hidden }: { hidden?: boolean }) => {
    return (
      <div
        className={classNames(styles.tradeButtons, {
          [styles.hidden]: hidden,
          [styles.compact]: isCompactLayout,
        })}
      >
        {!isMobile && !isWidgetOpen && (
          <ChartTradeButtons
            symbol={symbol}
            priceIncrement={priceIncrement}
            onTradeClick={onTradeClick}
            compact={isCompactLayout}
          />
        )}
        {isFullscreen && !isTablet && (
          <Button.Secondary
            iconRight={EditIcon}
            size='sm'
            onClick={() => {
              changeInstrumentModal.open({
                onSelect: onSelectInstrument,
                accountCode: account.platformOverview?.login,
              })
            }}
          >
            {symbol}
          </Button.Secondary>
        )}
        {isFullscreen && isTablet && (
          <IconButton.Secondary
            aria-label={symbol}
            icon={EditIcon}
            size={isSidebarExpanded ? 'xs' : 'sm'}
            onClick={() => {
              changeInstrumentModal.open({
                onSelect: onSelectInstrument,
                accountCode: account.platformOverview?.login,
              })
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullscreen]: isFullscreen,
      })}
      data-tour-target='trade.chart'
    >
      {!isMobile && <ChartControls hidden={isChartMenuOpen} />}
      {!collapsed && (
        <>
          {error && <div>{error}</div>}

          <TradeButtons hidden={isChartMenuOpen} />

          <div
            className={classNames(styles.chartWrapper, {
              [styles.hidden]: !!error,
            })}
            style={{ width, height: chartHeight }}
          >
            <div ref={chartContainerRef} className={styles.chartInnerWrapper} />
          </div>
        </>
      )}
    </div>
  )
}

type ChartTradeButtonsProps = {
  symbol?: string
  priceIncrement?: number | null
  onTradeClick?: (side: TradeSide, symbol: string) => void
  compact?: boolean
}

const ChartTradeButtons: React.FC<ChartTradeButtonsProps> = React.memo(
  ({ symbol, priceIncrement, onTradeClick, compact = false }) => {
    const { t } = useTranslation()
    const { formatNumber } = useFormatNumber()
    const sellPrice = useCurrentPriceForTrade(symbol, 'SELL', 'order')
    const buyPrice = useCurrentPriceForTrade(symbol, 'BUY', 'order')

    if (!symbol) return null

    const formatPrice = (price: number | null | undefined) => {
      if (price == null) return '—'
      const decimals = getStepDecimals(priceIncrement ?? price) ?? 0
      return formatNumber(price, { minDecimalPlaces: decimals, maxDecimalPlaces: decimals })
    }

    const sellLabel = compact
      ? t('Instruments.Sell')
      : `${t('Instruments.Sell')} ${formatPrice(sellPrice)}`
    const buyLabel = compact
      ? t('Instruments.Buy')
      : `${t('Instruments.Buy')} ${formatPrice(buyPrice)}`

    return (
      <>
        <Button.Danger
          size={compact ? 'xs' : 'sm'}
          tabularNums
          onClick={() => onTradeClick?.('SELL', symbol)}
        >
          {sellLabel}
        </Button.Danger>

        <Button.Success
          size={compact ? 'xs' : 'sm'}
          tabularNums
          onClick={() => onTradeClick?.('BUY', symbol)}
        >
          {buyLabel}
        </Button.Success>
      </>
    )
  }
)

const toCssLength = (v: string | number): string => {
  return typeof v === 'number' ? `${v}px` : v
}

const buildWidgetOptions = (params: {
  container: HTMLDivElement
  datafeed: ReturnType<typeof createDxtradeDatafeed>
  symbol: string
  locale: string
  theme: ChartTheme
  timeframe: string
  timezone?: string
}) => {
  const { container, datafeed, symbol, locale, theme, timeframe, timezone } = params
  const { bg, fg } = THEME_COLORS[theme]

  return {
    container,
    library_path: TV_LIBRARY_PATH,
    datafeed,
    symbol,
    interval: timeframe,
    locale,
    fullscreen: false,
    autosize: true,
    theme,
    debug: false,
    toolbar_bg: bg,
    timezone,
    loading_screen: { backgroundColor: bg, foregroundColor: fg },

    overrides: {
      'paneProperties.backgroundType': 'solid',
      'paneProperties.background': bg,
      'paneProperties.vertGridProperties.color': 'rgba(0, 0, 0, 0.06)',
      'paneProperties.horzGridProperties.color': 'rgba(0, 0, 0, 0.06)',
      'paneProperties.rightAxisProperties.visible': true,
      'paneProperties.rightAxisProperties.autoScale': true,
      'paneProperties.rightAxisProperties.alignLabels': true,
      'mainSeriesProperties.showPriceScale': true,
    },

    disabled_features: [...DISABLED_FEATURES],
    enabled_features: [...ENABLED_FEATURES],
  }
}

const injectChartStyles = (iframe: HTMLIFrameElement) => {
  try {
    const doc = iframe.contentDocument
    if (!doc || !doc.head) return

    const existing = doc.getElementById('tv-custom-styles')
    if (existing) existing.remove()

    const style = doc.createElement('style')
    style.id = 'tv-custom-styles'
    style.textContent = `
      button[aria-haspopup="menu"] {
        position: relative !important;
        z-index: 99999 !important;
        pointer-events: all !important;
      }

      button[aria-haspopup="menu"] > div {
        pointer-events: none !important;
        position: relative !important;
        z-index: 1 !important;
      }

      button[aria-haspopup="menu"] > * {
        pointer-events: none !important;
      }

      button[aria-haspopup="menu"] {
        pointer-events: all !important;
      }

      button[aria-haspopup="menu"][aria-expanded="true"],
      button[aria-haspopup="menu"].isOpened {
        pointer-events: all !important;
      }

      [role="toolbar"] {
        isolation: isolate !important;
      }
    `
    doc.head.appendChild(style)
  } catch (e) {
    console.warn('[TV] Failed to inject styles into iframe', e)
  }
}

ChartTradeButtons.displayName = 'ChartTradeButtons'
